import * as React from "react";
import { observer } from "mobx-react";

import {
  UitkExperimentalButtonTabs,
  UitkTab,
  UitkTabsType,
  UitkTabAnchorType,
} from "uitk-react-experimental-button-tabs";

import { useLocalization } from "@shared-ui/localization-context";

import { FlightHiddenSearchDataProps, WizardFlightPWAViewProps } from "./typings";
import { RoundtripFlight } from "./components/RoundtripFlight";
import { OnewayFlight } from "./components/OnewayFlight";
import { MulticityFlight } from "./components/MulticityFlight";
import { LobTitle } from "src/components/shared/LobTitle/LobTitle";
import { FlightHiddenSearchData } from "./components/FlightHiddenSearchData";
import { WizardFlightPWA } from "./WizardFlightPWA";
import { FHCHiddenSearchData } from "./components/FHCHiddenSearchData";
import { LobSubtitle } from "src/components/shared/LobSubtitle/LobSubtitle";
import { locKeys } from "./components/l10n";
import { FlightType } from "src/stores/wizard/state/flight/typings";
import { SubmitRow } from "components/shared/StorefrontWizard/SubmitRow/SubmitRow";
import { liveAnnounce, Level } from "uitk-react-utils";
import { Travelers } from "src/components/shared/Travelers/Travelers";
import { PreferredClassInput } from "components/shared/PreferredClassInput/PreferredClassInput";
import { TravelersTriggerTypes } from "src/components/shared/TravelersField/typings";
import {
  UitkLayoutConditionalGridSpan,
  UitkLayoutGrid,
  UitkLayoutGridItem,
  UitkLayoutConditionalGridColumnRow,
} from "uitk-react-layout-grid";
import { UitkLayoutFlex, UitkLayoutFlexItem } from "uitk-react-layout-flex";
import { Viewport, ViewMedium, ViewSmall } from "@shared-ui/viewport-context";

export const WizardFlightPWAView: React.FunctionComponent<WizardFlightPWAViewProps> = observer((props) => {
  const {
    flightWizardState,
    flightWizardState: { config: flightConfig },
  } = props.wizardState;
  const { formatText } = useLocalization();

  const tabIdPrefix = "wizard-flight-tab-";
  const { flightClassCode, listOfAdditionalLegs, subLOBState, wizardInputsArray } = flightWizardState;
  const currentTab = subLOBState; // tab state is calculated based on subLOB

  if (listOfAdditionalLegs.length > 1) {
    liveAnnounce(formatText(locKeys.multiCityFlightLegLabel, listOfAdditionalLegs.length + 1), Level.POLITE);
  }

  const flightHiddenSearchDataProps: FlightHiddenSearchDataProps = {
    flightType: "on",
    flightClass: WizardFlightPWA.getFlightClassCodeForURL(flightClassCode),
    mode: "search",
    trip: subLOBState,
    leg1: WizardFlightPWA.getLegURLFragment(flightWizardState, flightConfig)[0],
    leg2: WizardFlightPWA.getLegURLFragment(flightWizardState, flightConfig)[1],
    flightWizardState: props.wizardState.flightWizardState,
  };

  const setInputsRefs = (ref: React.RefObject<HTMLInputElement>, secondRef?: React.RefObject<HTMLInputElement>) => {
    wizardInputsArray.push(ref);
    if (secondRef) {
      wizardInputsArray.push(secondRef);
    }
  };

  const getLocError = (isLink: boolean) => {
    if (!isLink) {
      return flightWizardState.moreThanOneError()
        ? flightConfig.form.submitButton.locMultipleWarningsToken!
        : flightConfig.form.submitButton.locWarningToken!;
    }

    return flightWizardState.moreThanOneError()
      ? flightConfig.form.submitButton.locMultipleWarningsLinkToken
      : flightConfig.form.submitButton.locWarningLinkToken;
  };
  const travelersColSpan: UitkLayoutConditionalGridSpan = { small: 2, medium: 2, large: 2 };
  const travelersAndPreferredGridColumns: UitkLayoutConditionalGridColumnRow = { small: 6 };
  const travelersContainerColSpan: UitkLayoutConditionalGridSpan = { small: 2 };
  const preferredClassColSpan: UitkLayoutConditionalGridSpan = { small: 3, medium: 2, large: 2 };
  const { config, isAddAHotelChecked, isAddACarChecked } = flightWizardState;
  const travelerConfig =
    config.enableAddHotel && isAddAHotelChecked
      ? config.travelersWithHotel
      : config.enableAddCar && isAddACarChecked
      ? config.travelersWithCar
      : config.travelers;

  /**
   * Value used if no rehydratation(JS disabled)
   */
  const { noRehydratationValues } = flightConfig.travelers;

  /**
   * Set the new tab to display and update global state
   */
  const handleTabChange = (flightType: FlightType) => {
    props.onSelectFlightType(flightType);
  };

  /**
   * Builds the Travelers and PreferredClassInput
   * as a prop to be sent to UitkExperimentalButtonTabs
   * to display them next to the UitkTabs, at the right
   */
  const noNavigationItems = () => (
    <Viewport>
      <ViewMedium>
        <UitkLayoutFlex alignItems="center" space="six" className="all-x-padding-six">
          <UitkLayoutFlexItem>
            <Travelers
              lobState={flightWizardState}
              config={travelerConfig}
              colSpan={travelersColSpan}
              noRehydratationValues={noRehydratationValues}
              triggerType={TravelersTriggerTypes.LINK_BUTTON}
            />
          </UitkLayoutFlexItem>
          <UitkLayoutFlexItem>
            <PreferredClassInput
              colSpan={preferredClassColSpan}
              onSelectPreferredClassCode={props.onSelectPreferredClassCode}
              wizardState={props.wizardState}
            />
          </UitkLayoutFlexItem>
        </UitkLayoutFlex>
      </ViewMedium>
    </Viewport>
  );

  const referrerId =
    currentTab !== FlightType.MULTI_CITY ? flightConfig.referrerId : flightConfig.multiCityFlight.referrerId;

  const { pageHeading, pageSubHeadline } = props.compositionStore.composition!;

  const titleFromComposition = (props.flexComponent.model.titleFromPageHeader && pageHeading) || undefined;
  const subTitleFromComposition = (props.flexComponent.model.subTitleFromPageSubHeader && pageSubHeadline) || undefined;

  return (
    <form
      noValidate={true}
      className="WizardFlightPWA"
      action={
        flightWizardState.isAddAHotelChecked || flightWizardState.isAddACarChecked
          ? "/flexibleshopping"
          : "/Flights-Search"
      }
      onSubmit={flightWizardState.submit}
      id={flightConfig.elementId}
      data-fm={props.flexComponent.fmId}
      data-fm-title-id={props.flexComponent.fmTitleId}
    >
      {flightWizardState.isAddAHotelChecked || flightWizardState.isAddACarChecked ? (
        <FHCHiddenSearchData flightWizardState={props.wizardState.flightWizardState} />
      ) : (
        <FlightHiddenSearchData {...flightHiddenSearchDataProps} />
      )}

      <LobTitle lobState={flightWizardState} customTitle={titleFromComposition} />
      <LobSubtitle lobState={flightWizardState} customSubTitle={subTitleFromComposition} />
      {/* Each tab represents a flights sub LOB (roundtrip, one way and multicity) */}
      <UitkExperimentalButtonTabs
        className="all-t-margin-three flightTypeTabs"
        selectedTab={currentTab}
        onTabSelect={handleTabChange}
        tabsType={UitkTabsType.NATURAL}
        tabAnchorType={UitkTabAnchorType.BUTTON}
        noNavigationItems={noNavigationItems}
      >
        <UitkTab
          name={FlightType.ROUND_TRIP}
          displayName={formatText(locKeys.flightRoundTripText)}
          targetURI={`?flightType=${FlightType.ROUND_TRIP}`}
          id={`${tabIdPrefix}${FlightType.ROUND_TRIP}`}
        >
          {currentTab === FlightType.ROUND_TRIP && (
            <RoundtripFlight
              setInputsRefs={setInputsRefs}
              lobState={flightWizardState}
              flightConfig={flightConfig}
              getLocError={getLocError}
              {...props}
            />
          )}
        </UitkTab>

        <UitkTab
          name={FlightType.ONE_WAY}
          displayName={formatText(locKeys.flightOneWayText)}
          targetURI={`?flightType=${FlightType.ONE_WAY}`}
          id={`${tabIdPrefix}${FlightType.ONE_WAY}`}
        >
          {currentTab === FlightType.ONE_WAY && (
            <OnewayFlight
              setInputsRefs={setInputsRefs}
              lobState={flightWizardState}
              flightConfig={flightConfig}
              getLocError={getLocError}
              {...props}
            />
          )}
        </UitkTab>

        <UitkTab
          name={FlightType.MULTI_CITY}
          displayName={formatText(locKeys.flightMultiCityText)}
          targetURI={`?flightType=${FlightType.MULTI_CITY}`}
          id={`${tabIdPrefix}${FlightType.MULTI_CITY}`}
        >
          {currentTab === FlightType.MULTI_CITY && (
            <MulticityFlight
              setInputsRefs={setInputsRefs}
              lobState={flightWizardState}
              flightConfig={flightConfig}
              getLocError={getLocError}
              {...props}
            />
          )}
        </UitkTab>
      </UitkExperimentalButtonTabs>
      <Viewport>
        <ViewSmall>
          <UitkLayoutGrid columns={travelersAndPreferredGridColumns} className="all-t-padding-six">
            <UitkLayoutGridItem colSpan={travelersContainerColSpan}>
              <div>
                <Travelers
                  lobState={flightWizardState}
                  config={travelerConfig}
                  colSpan={travelersColSpan}
                  noRehydratationValues={noRehydratationValues}
                  triggerType={TravelersTriggerTypes.LINK_BUTTON}
                />
              </div>
            </UitkLayoutGridItem>
            <PreferredClassInput
              colSpan={preferredClassColSpan}
              onSelectPreferredClassCode={props.onSelectPreferredClassCode}
              wizardState={props.wizardState}
            />
          </UitkLayoutGrid>
        </ViewSmall>
        <ViewMedium />
      </Viewport>
      <SubmitRow lobState={flightWizardState} rfrr={referrerId} />
    </form>
  );
});
